import React from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
// @iconify
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import creditCardOutline from '@iconify/icons-eva/credit-card-outline';
import smartphoneFill from '@iconify/icons-eva/smartphone-fill';
import personFill from '@iconify/icons-eva/person-fill';

// constants
import { Status, statusColorMap } from '../constants/ocpi.constant';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    maxWidth: 700,
    width: '100%'
  }
}));

const DetailItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5)
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  wordBreak: 'break-all'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2)
}));

const TokenIcon = ({ tokenType, size = 40 }) => {
  const getIcon = () => {
    switch (tokenType) {
      case 'RFID':
        return creditCardOutline;
      case 'MOBILE':
        return smartphoneFill;
      case 'AD_HOC_USER':
        return personFill;
      default:
        return creditCardOutline;
    }
  };

  return <Icon icon={getIcon()} width={size} height={size} />;
};

TokenIcon.propTypes = {
  tokenType: PropTypes.string.isRequired,
  size: PropTypes.number
};

const TokenView = ({ open, onClose, token, onEdit }) => {
  if (!token) return null;

  // Map OCPI token data to display format
  const displayData = {
    id: token.id || token.uid,
    tokenUid: token.uid || token.token_uid || 'N/A',
    tokenType: token.type || token.token_type || 'RFID',
    countryCode: token.country_code || token.partner_country_code || 'N/A',
    partyId: token.party_id || token.partner_party_id || 'N/A',
    contractId: token.contract_id || 'N/A',
    issuer: token.issuer || token.partner_name || 'N/A',
    profile: token.profile || 'N/A',
    visualNumber: token.visual_number || 'N/A',
    whitelistType: token.whitelist_type || 'N/A',
    groupId: token.group_id || 'N/A',
    language: token.language || 'N/A',
    defaultProfileType: token.default_profile_type || 'N/A',
    energyContract: token.energy_contract || null,
    isValid: token.valid !== undefined ? token.valid : true,
    status: token.valid ? Status.VALID : Status.INVALID,
    lastUpdated: token.last_updated || token.updated_at || new Date().toISOString()
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TokenIcon tokenType={displayData.tokenType} />
            <Box>
              <Typography variant="h6">
                Token Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {displayData.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Icon icon={closeFill} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Status and Basic Info */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Status</Typography>
                <Chip
                  label={displayData.isValid ? Status.VALID : Status.INVALID}
                  color={statusColorMap[displayData.status].color}
                  variant="filled"
                  sx={{ 
                    fontWeight: 600,
                    height: 24,
                    bgcolor: statusColorMap[displayData.status].bgcolor,
                    color: statusColorMap[displayData.status].text,
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Last Updated: {new Date(displayData.lastUpdated).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12}>
            <SectionTitle variant="subtitle1">
              Basic Information
            </SectionTitle>
          </Grid>

          <Grid item xs={6}>
            <DetailItem>
              <DetailLabel variant="body2">Country Code</DetailLabel>
              <DetailValue variant="body1">{displayData.countryCode}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={6}>
            <DetailItem>
              <DetailLabel variant="body2">Party ID</DetailLabel>
              <DetailValue variant="body1">{displayData.partyId}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12}>
            <DetailItem>
              <DetailLabel variant="body2">Token UID</DetailLabel>
              <DetailValue variant="body1">{displayData.tokenUid}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={6}>
            <DetailItem>
              <DetailLabel variant="body2">Token Type</DetailLabel>
              <Chip
                label={displayData.tokenType}
                variant="outlined"
                color="primary"
                size="small"
              />
            </DetailItem>
          </Grid>

          <Grid item xs={6}>
            <DetailItem>
              <DetailLabel variant="body2">Whitelist Type</DetailLabel>
              <Chip
                label={displayData.whitelistType}
                variant="outlined"
                color="secondary"
                size="small"
              />
            </DetailItem>
          </Grid>

          <Grid item xs={12}>
            <DetailItem>
              <DetailLabel variant="body2">Contract ID</DetailLabel>
              <DetailValue variant="body1">{displayData.contractId}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12}>
            <DetailItem>
              <DetailLabel variant="body2">Issuer</DetailLabel>
              <DetailValue variant="body1">{displayData.issuer}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={6}>
            <DetailItem>
              <DetailLabel variant="body2">Profile</DetailLabel>
              <Chip
                label={displayData.profile}
                variant="outlined"
                color="info"
                size="small"
              />
            </DetailItem>
          </Grid>

          <Grid item xs={6}>
            <DetailItem>
              <DetailLabel variant="body2">Visual Number</DetailLabel>
              <DetailValue variant="body1">{displayData.visualNumber}</DetailValue>
            </DetailItem>
          </Grid>

          {/* Optional Information */}
          {(displayData.visualNumber || displayData.groupId || displayData.defaultProfileType) && (
            <>
              <Grid item xs={12}>
                <Divider />
                <SectionTitle variant="subtitle1">
                  Optional Information
                </SectionTitle>
              </Grid>

              {displayData.visualNumber && (
                <Grid item xs={6}>
                  <DetailItem>
                    <DetailLabel variant="body2">Visual Number</DetailLabel>
                    <DetailValue variant="body1">{displayData.visualNumber}</DetailValue>
                  </DetailItem>
                </Grid>
              )}

              {displayData.groupId && (
                <Grid item xs={6}>
                  <DetailItem>
                    <DetailLabel variant="body2">Group ID</DetailLabel>
                    <DetailValue variant="body1">{displayData.groupId}</DetailValue>
                  </DetailItem>
                </Grid>
              )}

              {displayData.defaultProfileType && (
                <Grid item xs={12}>
                  <DetailItem>
                    <DetailLabel variant="body2">Default Profile Type</DetailLabel>
                    <DetailValue variant="body1">{displayData.defaultProfileType}</DetailValue>
                  </DetailItem>
                </Grid>
              )}
            </>
          )}

          {/* Energy Contract */}
          {displayData.energyContract && (
            <>
              <Grid item xs={12}>
                <Divider />
                <SectionTitle variant="subtitle1">
                  Energy Contract
                </SectionTitle>
              </Grid>

              {displayData.energyContract.supplierName && (
                <Grid item xs={6}>
                  <DetailItem>
                    <DetailLabel variant="body2">Supplier Name</DetailLabel>
                    <DetailValue variant="body1">{displayData.energyContract.supplierName}</DetailValue>
                  </DetailItem>
                </Grid>
              )}

              {displayData.energyContract.contractId && (
                <Grid item xs={6}>
                  <DetailItem>
                    <DetailLabel variant="body2">Energy Contract ID</DetailLabel>
                    <DetailValue variant="body1">{displayData.energyContract.contractId}</DetailValue>
                  </DetailItem>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button onClick={onEdit} variant="contained" color="primary">
          Edit Token
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

TokenView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.shape({
    id: PropTypes.string,
    uid: PropTypes.string,
    token_uid: PropTypes.string,
    type: PropTypes.string,
    token_type: PropTypes.string,
    country_code: PropTypes.string,
    party_id: PropTypes.string,
    partner_country_code: PropTypes.string,
    partner_party_id: PropTypes.string,
    contract_id: PropTypes.string,
    issuer: PropTypes.string,
    partner_name: PropTypes.string,
    profile: PropTypes.string,
    visual_number: PropTypes.string,
    whitelist_type: PropTypes.string,
    group_id: PropTypes.string,
    language: PropTypes.string,
    default_profile_type: PropTypes.string,
    energy_contract: PropTypes.object,
    valid: PropTypes.bool,
    last_updated: PropTypes.string,
    updated_at: PropTypes.string
  }),
  onEdit: PropTypes.func.isRequired
};

export default TokenView;
